// src/components/charts/MetricTrendChart.jsx
import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import LoadingBouncyDots from "../ui/LoadingBouncyDots";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { MinMaxBarsPlugin } from "../../utils/plugins/minMaxBars";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  MinMaxBarsPlugin,
);

/**
 * Interactive trend chart
 * - Click a point to toggle-remove/restore it from the series.
 * - Shift/Cmd/Ctrl-click for multi-select.
 * - Removed points are rendered as null (excluded from the line).
 */
function MetricTrendChart({
  height = 200,
  labels = [],
  values = [],
  minMax = null,
  showMinMaxBars = true,
  minMaxWidth = 10,
  lineLabel = "",
  yUnit = "",
  loading = false,
  yMin,
  yMax,
  yTickStep,
  yPadPct = 0.12,
  yPadAbs = 0,
  yDecimals = 0,

  // NEW: controlled/uncontrolled support
  removedIndices: removedIndicesProp, // optional: array<number>
  onRemovedChange, // optional: (removedIndices: number[]) => void

  // NEW: behaviour
  spanGaps = true, // if false, line breaks at nulls
}) {
  const chartRef = useRef(null);

  const hasData =
    Array.isArray(values) && values.some((v) => Number.isFinite(v));

  // Internal removed set (uncontrolled) + sync when controlled prop supplied
  const [removedSet, setRemovedSet] = useState(() => new Set());
  useEffect(() => {
    if (Array.isArray(removedIndicesProp)) {
      setRemovedSet(new Set(removedIndicesProp));
    }
  }, [removedIndicesProp]);

  // NEW: selection set for multi-point operations/feedback
  const [selectedSet, setSelectedSet] = useState(() => new Set());

  // Keep selection indices valid if data length changes
  useEffect(() => {
    setSelectedSet((prev) => {
      const next = new Set();
      for (const i of prev)
        if (i >= 0 && i < (values?.length ?? 0)) next.add(i);
      return next;
    });
    setRemovedSet((prev) => {
      const next = new Set();
      for (const i of prev)
        if (i >= 0 && i < (values?.length ?? 0)) next.add(i);
      return next;
    });
  }, [values?.length]);

  const emitRemovedChange = useCallback(
    (nextSet) => {
      if (typeof onRemovedChange === "function") {
        onRemovedChange(Array.from(nextSet).sort((a, b) => a - b));
      }
    },
    [onRemovedChange],
  );

  // Collect all numbers influencing y-range, but IGNORE removed points (important)
  const [seriesMin, seriesMax] = useMemo(() => {
    const nums = [];
    for (let i = 0; i < (values || []).length; i++) {
      if (removedSet.has(i)) continue;
      const v = values[i];
      if (Number.isFinite(v)) nums.push(v);
    }
    if (Array.isArray(minMax)) {
      for (let i = 0; i < minMax.length; i++) {
        if (removedSet.has(i)) continue;
        const mm = minMax[i];
        const lo = Array.isArray(mm) ? mm[0] : mm?.min;
        const hi = Array.isArray(mm) ? mm[1] : mm?.max;
        if (Number.isFinite(lo)) nums.push(lo);
        if (Number.isFinite(hi)) nums.push(hi);
      }
    }
    if (!nums.length) return [0, 1];
    return [Math.min(...nums), Math.max(...nums)];
  }, [values, minMax, removedSet]);

  const roundDownTo = (v, step) => Math.floor(v / step) * step;
  const roundUpTo = (v, step) => Math.ceil(v / step) * step;

  const [minAuto, maxAuto] = useMemo(() => {
    let min = seriesMin;
    let max = seriesMax;
    const span = Math.max(max - min, 1);
    const pad = Math.max(span * yPadPct, yPadAbs);
    min -= pad;
    max += pad;

    if (Number.isFinite(yTickStep) && yTickStep > 0) {
      min = roundDownTo(min, yTickStep);
      max = roundUpTo(max, yTickStep);
    } else {
      const span2 = Math.max(max - min, 1e-9);
      const pow = Math.pow(10, Math.floor(Math.log10(span2)));
      const niceStep = span2 / 6;
      const candidates = [1, 2, 5].map((m) => m * pow);
      const step = candidates.reduce(
        (best, c) =>
          Math.abs(c - niceStep) < Math.abs(best - niceStep) ? c : best,
        candidates[0],
      );
      min = Math.floor(min / step) * step;
      max = Math.ceil(max / step) * step;
    }
    return [min, max];
  }, [seriesMin, seriesMax, yPadPct, yPadAbs, yTickStep]);

  const primary = getPrimaryColour();

  // NEW: create a view of values with removed points nulled out
  const plottedValues = useMemo(() => {
    if (!Array.isArray(values)) return [];
    return values.map((v, i) => (removedSet.has(i) ? null : v));
  }, [values, removedSet]);

  // NEW: per-point styling for selection feedback
  const pointRadius = useMemo(() => {
    const n = plottedValues.length || (labels?.length ?? 0);
    const arr = new Array(n).fill(hasData ? 3 : 0);
    for (const i of selectedSet) {
      if (i >= 0 && i < arr.length) arr[i] = 5;
    }
    return arr;
  }, [plottedValues.length, labels?.length, hasData, selectedSet]);

  const pointBg = useMemo(() => {
    const n = plottedValues.length || (labels?.length ?? 0);
    const arr = new Array(n).fill(primary);
    // Selected points slightly darker: keep simple (no explicit colors if you prefer)
    // Using same primary is fine; radius alone communicates selection.
    return arr;
  }, [plottedValues.length, labels?.length, primary]);

  const chartData = useMemo(() => {
    const safeLabels = hasData
      ? labels
      : labels.length
        ? labels
        : ["", "", "", "", "", ""];

    const n = safeLabels.length || 6;

    const dataArr = hasData
      ? plottedValues
      : new Array(labels.length || 6).fill(null);

    const ds = {
      label: lineLabel || yUnit || "Trend",
      data: dataArr,
      borderColor: primary,
      pointBackgroundColor: pointBg,
      pointBorderColor: primary,
      pointRadius: pointRadius,
      pointHoverRadius: hasData ? 6 : 0,
      borderWidth: 2,
      spanGaps,
      tension: 0.25,
      ...(Array.isArray(minMax) && minMax.length === n
        ? { _minMax: minMax }
        : {}),
    };

    return { labels: safeLabels, datasets: [ds] };
  }, [
    hasData,
    labels,
    plottedValues,
    minMax,
    lineLabel,
    yUnit,
    primary,
    pointRadius,
    pointBg,
    spanGaps,
  ]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: hasData },
        ...(showMinMaxBars
          ? {
              minMaxBars: {
                enabled: true,
                lineWidth: minMaxWidth,
                color: primary,
                capStyle: "round",
              },
            }
          : {}),
      },
      scales: {
        x: {
          grid: { color: "rgba(0,0,0,0.05)", drawBorder: false },
          border: { display: false },
          ticks: { color: "rgba(0,0,0,0.5)", autoSkip: true, maxTicksLimit: 6 },
          title: { display: false },
        },
        y: {
          grace: "8%",
          grid: { color: "rgba(0,0,0,0.06)", drawBorder: false },
          border: { display: false },
          min: Number.isFinite(yMin) ? yMin : minAuto,
          max: Number.isFinite(yMax) ? yMax : maxAuto,
          ticks: {
            color: "rgba(0,0,0,0.5)",
            stepSize:
              Number.isFinite(yTickStep) && yTickStep > 0
                ? yTickStep
                : undefined,
            callback: (v) => {
              const n = Number(v);
              const text = Number.isFinite(n) ? n.toFixed(yDecimals) : v;
              return yUnit ? `${text}` : text;
            },
          },
          title: {
            display: !!yUnit,
            text: yUnit,
            color: "rgba(0,0,0,0.65)",
          },
        },
      },
      animation: { duration: 250 },

      // NEW: improve clickability
      interaction: { mode: "nearest", intersect: true },
      hover: { mode: "nearest", intersect: true },
    }),
    [
      hasData,
      yMin,
      yMax,
      yUnit,
      minAuto,
      maxAuto,
      yTickStep,
      yDecimals,
      showMinMaxBars,
      minMaxWidth,
      primary,
    ],
  );

  // NEW: click handler to toggle points
  const onClick = useCallback(
    (event) => {
      const chart = chartRef.current;
      if (!chart) return;

      // Chart.js v3/v4
      const elements = chart.getElementsAtEventForMode(
        event.nativeEvent,
        "nearest",
        { intersect: true },
        true,
      );

      if (!elements?.length) {
        // click empty space clears selection
        setSelectedSet(new Set());
        return;
      }

      const { index, datasetIndex } = elements[0];
      if (datasetIndex !== 0) return;

      const multi =
        event.nativeEvent.shiftKey ||
        event.nativeEvent.metaKey ||
        event.nativeEvent.ctrlKey;

      // update selection
      setSelectedSet((prev) => {
        const next = multi ? new Set(prev) : new Set();
        if (next.has(index)) next.delete(index);
        else next.add(index);
        return next;
      });

      // toggle removal if not multi-select, or if user clicks again with multi?
      // Here: always toggle removal for the clicked point.
      setRemovedSet((prev) => {
        const next = new Set(prev);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        emitRemovedChange(next);
        return next;
      });
    },
    [emitRemovedChange],
  );

  return (
    <div className="relative" style={{ height }}>
      {!hasData && !loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="text-xs text-typography/60">
            No data yet — record to see trends
          </div>
        </div>
      )}
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <LoadingBouncyDots text="" />
        </div>
      )}

      {/* NEW: onClick wires Chart.js hit-testing */}
      <Line
        ref={chartRef}
        data={chartData}
        options={options}
        onClick={onClick}
      />
    </div>
  );
}

export default memo(MetricTrendChart);
